import React from 'react';
import { Link } from 'react-router-dom';
import BonacciStudioIcon from './BonacciStudioIcon';
import AuroraFlower from './AuroraFlower';

/**
 * PublicShell — chrome for all pre-login pages. Provides the
 * branded nav (icon + Studio lockup), the Fibonacci-flower
 * aurora background, and the standard legal/footer strip.
 *
 * Props:
 *   children — page body content
 *   navRight — element rendered on the right side of nav (e.g., CTA, "Back" link)
 *   showFooter — include legal/footer strip (default true)
 *   maxWidth — Tailwind max-w-* class for the inner container (default 'max-w-6xl')
 *   flowerFocusX — horizontal center of the flower bloom, 0–1 (default 0.5)
 */
const PublicShell = ({
  children,
  navRight = null,
  showFooter = true,
  maxWidth = 'max-w-6xl',
  flowerFocusX = 0.5,
}) => {
  return (
    <div
      className="bs-public min-h-screen w-full bg-[#060509] text-white flex flex-col relative overflow-hidden"
      style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}
    >
      {/* Aurora Fibonacci-flower background */}
      <AuroraFlower focusX={flowerFocusX} />

      {/* Nav */}
      <nav className="relative z-20 border-b border-white/[0.08] bg-[#060509]/60 backdrop-blur-md">
        <div className={`${maxWidth} mx-auto px-6 py-4 flex justify-between items-center`}>
          <Link to="/" className="no-underline bs-text inline-flex items-center gap-2.5">
            <BonacciStudioIcon size={26} />
            <span
              className="text-base tracking-tight"
              style={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}
            >
              Studio
            </span>
          </Link>
          <div className="flex items-center gap-2">{navRight}</div>
        </div>
      </nav>

      {/* Main */}
      <main className="relative z-10 flex-1 flex flex-col">{children}</main>

      {/* Footer */}
      {showFooter && (
        <footer className="relative z-10 px-6 py-8 border-t border-white/[0.08]">
          <div className={`${maxWidth} mx-auto flex flex-col sm:flex-row justify-between items-center gap-3`}>
            <div className="flex items-center gap-2 text-white/40">
              <BonacciStudioIcon size={18} spiral={false} />
              <span className="text-sm" style={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}>
                Studio
              </span>
            </div>
            <div className="flex gap-5 text-sm text-white/40">
              <a href="/legal?doc=terms" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Terms</a>
              <a href="/legal?doc=privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Privacy</a>
              <a href="/legal?doc=refund" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Refund</a>
              <Link to="/pricing" className="hover:text-white/70 transition-colors">Pricing</Link>
            </div>
            <div className="text-xs text-white/30 text-center sm:text-right">&copy; 2026 ThinkingDBx Pvt. Ltd.</div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PublicShell;
